import { createReducer, on } from '@ngrx/store';
import { Facility } from '../../model/facility.model';
import {
  loadFacilitiesAtStartupError,
  loadFacilitiesAtStartupSuccess,
  loadFacilities,
  loadFacilitiesError,
  loadFacilitiesSuccess,
  loadFacilityByIdSuccess,
  loadFacilityGroupsSuccess,
  loadFacilitiesAtStartup,
  loadFacilityById,
  loadFacilityPerformanceBuildingByFacilityIdSuccess,
  loadFacilityPerformancesBuildingByFacilityGroupIdError,
  loadFacilityPerformanceBuildingByFacilityId,
  loadFacilityPerformancesBuildingByFacilityGroupIdSuccess,
  loadFacilityPerformancesBuildingByFacilityGroupId,
  loadFacilityPerformanceBuildingByFacilityIdError,
  loadFacilityPerformanceSolarByFacilityGroupId,
  loadFacilityPerformanceSolarByFacilityId,
  loadFacilityPerformanceSolarByFacilityGroupIdSuccess,
  loadFacilityPerformanceSolarByFacilityIdSuccess,
  loadFacilityPerformanceSolarByFacilityGroupIdError,
  loadFacilityPerformanceSolarByFacilityIdError,
  updateFacilities,
  setCurrentFacilityGroup,
  clearCurrentFacilityGroup,
  setCurrentFacilityById,
  setCurrentFacilityGroupById,
  setCurrentGroupAndFacilityByIds,
  updateFacilityAlertWithSnooze,
  updateFacilityAlertUnsnoozed,
  updateFacilityAlertsClosed,
  updateFacilityAlertReopened,
  updateFacility,
  updateFacilitySuccess,
  updateFacilityError,
  loadFacilitiesAndSetCurrentFacilityByIdSuccess,
  updateFacilityGroupSuccess,
  addFacilityBuildingsSuccess,
  updateFacilityBuildingsSuccess,
  deleteFacilityBuildingsSuccess,
  createFacilitySuccess,
} from '../actions/facility.actions';
import { FacilityGroup } from '../../model/facility-group.model';
import { FacilityPerformanceYearly, FacilitySolarPerformance } from 'brain-data/model/facility-performance.model';
import { AlertStatus } from 'brain-data/model/alert.model';

export default interface FacilityState {
  isSavingLoading?: boolean;
  facilityGroups: FacilityGroup[];
  facilities: Facility[];
  /** True once a facilities load has returned, so consumers can tell "empty" from "not loaded yet". */
  facilitiesLoaded: boolean;
  facilityGroup?: FacilityGroup;
  facility?: Facility;
  facilityPerformanceLoading: boolean;
  facilityPerformance: FacilityPerformanceYearly[];
  facilitySolarPerformance: FacilitySolarPerformance[];
}

export const initialState: FacilityState = {
  isSavingLoading: undefined,
  facilityGroups: [],
  facilities: [],
  facilitiesLoaded: false,
  facility: undefined,
  facilityGroup: undefined,
  facilityPerformanceLoading: false,
  facilityPerformance: [],
  facilitySolarPerformance: [],
};

export const reducer = createReducer(
  initialState,
  on(loadFacilityById, (state): FacilityState => ({ ...state, facility: undefined })),
  // Deliberately keeps the current facilities while a reload is in flight: blanking them made the
  // header search return nothing for the seconds GET /facilities takes, and left it empty on error.
  on(loadFacilities, loadFacilitiesAtStartup, (state): FacilityState => state),
  // Newly created facility is searchable immediately, without waiting for the reload.
  on(createFacilitySuccess, (state, { facility }): FacilityState => {
    const withoutCreated = state.facilities.filter(existing => existing.id !== facility.id);
    return { ...state, facilities: sortFacilitiesByName([...withoutCreated, facility]) };
  }),
  on(loadFacilitiesAtStartupSuccess, (state, { facilities, facilityGroups }): FacilityState => {
    const sortedFacilities = sortFacilitiesByName(facilities);
    return { ...state, facilities: sortedFacilities, facilityGroups, facilitiesLoaded: true };
  }),
  on(
    loadFacilitiesAndSetCurrentFacilityByIdSuccess,
    (state, { facilities }): FacilityState => ({ ...state, facilities, facilitiesLoaded: true, isSavingLoading: false }),
  ),
  on(loadFacilityByIdSuccess, (state, { facility }): FacilityState => ({ ...state, facility })),
  on(loadFacilityGroupsSuccess, (state, { facilityGroups }): FacilityState => ({ ...state, facilityGroups })),
  on(loadFacilitiesSuccess, (state, { facilities }): FacilityState => ({ ...state, facilities, facilitiesLoaded: true })),
  // A failed load still counts as "load finished" so splash screens keyed on facilitiesLoaded
  // dismiss instead of spinning forever (matters for brand-new tenants and transient API errors).
  on(loadFacilitiesError, loadFacilitiesAtStartupError, (state): FacilityState => ({ ...state, facilitiesLoaded: true })),
  on(loadFacilityPerformancesBuildingByFacilityGroupId, (state): FacilityState => ({ ...state, facilityPerformance: [], facilityPerformanceLoading: true })),
  on(
    loadFacilityPerformancesBuildingByFacilityGroupIdSuccess,
    (state, { facilityGroupPerformance }): FacilityState => ({ ...state, facilityPerformance: facilityGroupPerformance, facilityPerformanceLoading: false }),
  ),
  on(
    loadFacilityPerformancesBuildingByFacilityGroupIdError,
    (state): FacilityState => ({ ...state, facilityPerformance: [], facilityPerformanceLoading: false }),
  ),
  on(loadFacilityPerformanceBuildingByFacilityId, (state): FacilityState => ({ ...state, facilityPerformance: [], facilityPerformanceLoading: true })),
  on(
    loadFacilityPerformanceBuildingByFacilityIdSuccess,
    (state, { facilityPerformance }): FacilityState => ({ ...state, facilityPerformance: [facilityPerformance], facilityPerformanceLoading: false }),
  ),
  on(loadFacilityPerformanceBuildingByFacilityIdError, (state): FacilityState => ({ ...state, facilityPerformance: [], facilityPerformanceLoading: false })),
  on(
    loadFacilityPerformanceSolarByFacilityGroupId,
    loadFacilityPerformanceSolarByFacilityId,
    (state): FacilityState => ({ ...state, facilitySolarPerformance: [], facilityPerformanceLoading: true }),
  ),
  on(
    loadFacilityPerformanceSolarByFacilityGroupIdSuccess,
    loadFacilityPerformanceSolarByFacilityIdSuccess,
    (state, { facilitySolarPerformance }): FacilityState => ({ ...state, facilitySolarPerformance, facilityPerformanceLoading: false }),
  ),
  on(
    loadFacilityPerformanceSolarByFacilityGroupIdError,
    loadFacilityPerformanceSolarByFacilityIdError,
    (state): FacilityState => ({ ...state, facilitySolarPerformance: [], facilityPerformanceLoading: false }),
  ),
  on(updateFacilities, (state, { facilities }): FacilityState => ({ ...state, facilities })),
  on(setCurrentFacilityGroup, (state, { facilityGroup }): FacilityState => ({ ...state, facilityGroup })),
  on(clearCurrentFacilityGroup, (state): FacilityState => ({ ...state, facilityGroup: undefined })),
  on(
    setCurrentGroupAndFacilityByIds,
    (state, { facilityGroupId, facilityId }): FacilityState => ({
      ...state,
      facilityGroup: findFacilityGroupById(facilityGroupId, state.facilityGroups),
      facility: state.facilities.find(f => f.id === facilityId),
    }),
  ),
  on(
    setCurrentFacilityGroupById,
    (state, { facilityGroupId }): FacilityState => ({
      ...state,
      facilityGroup: findFacilityGroupById(facilityGroupId, state.facilityGroups),
      facility: undefined,
    }),
  ),

  on(updateFacilityGroupSuccess, (state, { facilityGroupId, updatedFacilityGroup }): FacilityState => {
    const mergeGroupUpdates = (group: FacilityGroup): FacilityGroup => {
      const shouldUpdate = group.id === facilityGroupId;
      const updatedSubGroups = group.subGroups && group.subGroups.length > 0 ? group.subGroups.map(mergeGroupUpdates) : group.subGroups;

      if (!shouldUpdate) {
        if (updatedSubGroups !== group.subGroups) {
          return { ...group, subGroups: updatedSubGroups };
        }
        return group;
      }

      return {
        ...group,
        description: updatedFacilityGroup.description ?? group.description,
        name: updatedFacilityGroup.name ?? group.name,
        icon: updatedFacilityGroup.icon ?? group.icon,
        iconColor: updatedFacilityGroup.iconColor ?? group.iconColor,
        subGroups: updatedSubGroups,
      };
    };

    return {
      ...state,
      facilityGroups: state.facilityGroups.map(mergeGroupUpdates),
    };
  }),

  on(
    setCurrentFacilityById,
    (state, { facilityId }): FacilityState => ({
      ...state,
      facility: state.facilities.find(f => f.id === facilityId),
    }),
  ),
  on(updateFacilityAlertWithSnooze, (state, { alertId, snoozedUntil }) => {
    return {
      ...state,
      facilities: state.facilities.map(facility => {
        const alertExistsInFacility = facility.alerts.some(alert => alert.id === alertId);

        if (!alertExistsInFacility) {
          return facility;
        }

        const updatedAlerts = facility.alerts.map(alert => {
          if (alert.id !== alertId) {
            return alert;
          }

          return { ...alert, snoozedUntil };
        });

        return { ...facility, alerts: updatedAlerts };
      }),
    };
  }),
  on(updateFacilityAlertUnsnoozed, (state, { alertId }) => {
    return {
      ...state,
      facilities: state.facilities.map(facility => {
        if (!facility.alerts.some(alert => alert.id === alertId)) {
          return facility;
        }

        return {
          ...facility,
          alerts: facility.alerts.map(alert => (alert.id === alertId ? { ...alert, snoozedUntil: undefined } : alert)),
        };
      }),
    };
  }),
  on(updateFacilityAlertsClosed, (state, { alertIds }) => {
    const idsToClose = new Set(alertIds);
    return {
      ...state,
      facilities: state.facilities.map(facility => {
        if (!facility.alerts.some(alert => idsToClose.has(alert.id))) {
          return facility;
        }

        return {
          ...facility,
          alerts: facility.alerts.map(alert => (idsToClose.has(alert.id) ? { ...alert, status: AlertStatus.closed, snoozedUntil: undefined } : alert)),
        };
      }),
    };
  }),
  on(updateFacilityAlertReopened, (state, { alertId }) => {
    return {
      ...state,
      facilities: state.facilities.map(facility => {
        if (!facility.alerts.some(alert => alert.id === alertId)) {
          return facility;
        }

        return {
          ...facility,
          alerts: facility.alerts.map(alert => (alert.id === alertId ? { ...alert, status: AlertStatus.open, snoozedUntil: undefined } : alert)),
        };
      }),
    };
  }),
  on(updateFacility, (state): FacilityState => ({ ...state, isSavingLoading: true })),
  on(updateFacilitySuccess, (state): FacilityState => ({ ...state, isSavingLoading: false })),
  on(updateFacilityError, (state): FacilityState => ({ ...state, isSavingLoading: false })),

  on(
    addFacilityBuildingsSuccess,
    (state, { facilityId, buildings }): FacilityState => ({
      ...state,
      facilities: state.facilities.map(f => (f.id === facilityId ? { ...f, buildings: [...f.buildings, ...buildings] } : f)),
      facility:
        state.facility && state.facility.id === facilityId
          ? { ...state.facility, buildings: [...(state.facility.buildings || []), ...buildings] }
          : state.facility,
    }),
  ),

  on(
    updateFacilityBuildingsSuccess,
    (state, { facilityId, buildings }): FacilityState => ({
      ...state,
      facilities: state.facilities.map(f =>
        f.id === facilityId
          ? {
              ...f,
              buildings: f.buildings.map(b => {
                const nb = buildings.find(x => x.id === b.id);
                return nb ? nb : b;
              }),
            }
          : f,
      ),
      facility:
        state.facility && state.facility.id === facilityId
          ? {
              ...state.facility,
              buildings: state.facility.buildings.map(b => {
                const nb = buildings.find(x => x.id === b.id);
                return nb ? nb : b;
              }),
            }
          : state.facility,
    }),
  ),

  on(
    deleteFacilityBuildingsSuccess,
    (state, { facilityId, ids }): FacilityState => ({
      ...state,
      facilities: state.facilities.map(f => (f.id === facilityId ? { ...f, buildings: f.buildings.filter(b => !ids.includes(b.id!)) } : f)),
      facility:
        state.facility && state.facility.id === facilityId
          ? { ...state.facility, buildings: state.facility.buildings.filter(b => !ids.includes(b.id!)) }
          : state.facility,
    }),
  ),
);

export const findFacilityGroupById = (id: number, facilityGroups: FacilityGroup[]): FacilityGroup | undefined => {
  for (const group of facilityGroups) {
    if (group.id === id) {
      return group;
    }
    if (group.subGroups.length) {
      const subgroup = findFacilityGroupById(id, group.subGroups);
      if (subgroup) {
        return subgroup;
      }
    }
  }
  return undefined;
};

export const sortFacilitiesByName = (facilities: Facility[]): Facility[] => {
  return [...facilities].sort((a, b) => {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();

    const matchA = nameA.match(/\d+/);
    const matchB = nameB.match(/\d+/);

    const numA = matchA ? parseInt(matchA[0], 10) : null;
    const numB = matchB ? parseInt(matchB[0], 10) : null;

    if (numA !== null && numB !== null) {
      if (numA === numB) {
        return nameA.localeCompare(nameB);
      }
      return numA - numB;
    } else if (numA !== null) {
      return -1;
    } else if (numB !== null) {
      return 1;
    } else {
      return nameA.localeCompare(nameB);
    }
  });
};
